import { useEffect, useState } from 'react';

import { Scrollbars } from 'react-custom-scrollbars';
import 'react-upload-gallery/style.scss';

import { validateFileName } from '@helpers';

import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

import NibButton from '../NibButton/NibButton';
import NibDialog from '../NibDialog/NibDialog';
import NibInput from '../NibInput/NibInput';
import GallerySelectImageTable from './GallerySelectImageTable';
import RUG from './UploadGallery';

const BACKEND_URL = process.env.REACT_APP_API_URL;

const InputImageGallery = ({
  tools = ['info', 'add'],
  accept = ['jpg', 'jpeg', 'png'],
  formik,
  required = false,
  srcName,
  toRemoveName,
  newImagesName,
  placeholder = '',
  label = '',
  onChange = false,
  helperText,
}) => {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [initialState, setInitialState] = useState([]);
  const [images, setImages] = useState([]);
  const [toRemove, setToRemove] = useState([]);
  const [showMainForm, setMainFormDisplay] = useState(true);
  const [dialogUseButton, setdialogUseButton] = useState(true);
  const [dialogTitle, setDialogTitle] = useState('Set up your images');
  const [dialogSubTitle, setDialogSubtitle] = useState(
    'Please select images from the image vault or upload a new one by clicking on the plus button below',
  );
  const setUpImageWeightDialog = 480;
  const selectImageWeightDialog = 960;
  const [dialogWeight, setDialogWeight] = useState(setUpImageWeightDialog);
  const [pendingToRemove, setPendingToRemove] = useState([]);
  const [allImages, setAllImages] = useState([]);

  useEffect(() => {
    if (formik.values[srcName].length) {
      let payload = [];
      for (const image of formik.values[srcName]) {
        if (image.inited) {
          payload.push(image);
          continue;
        }
        payload.push({
          id: image.id,
          name: image.name || image.name.split('-')[1],
          size: image.size || `${image.size / 1000}kb`,
          source: image.source || image.url || `${BACKEND_URL}${image.url}`,
          inited: image.inited || true,
        });
      }
      setInitialState(payload);
      setAllImages(payload);
    }
  }, [formik.values[srcName]]);

  const onAdd = () => {
    setDialogOpen(true);
  };

  const falseRequest = ({ file, uid, onProgress, onSuccess }) => {
    const response = { url: URL.createObjectURL(file) };
    onProgress(uid, response);
    onSuccess(uid, response);

    const newImage = { uid, file, name: file.name, size: `${file.size / 1000}kb`, source: response.url, inited: true };
    setImages((prev) => [...prev, newImage]);
    setAllImages((prev) => [...prev, newImage]);
    return {};
  };

  const onChng = (images) => {
    for (const image of images) {
      if (!image.done) image.done = true;
      if (!image.uploading) image.uploading = true;
    }
  };

  const onRemove = (currentImage) => {
    if (currentImage.hasOwnProperty('id')) {
      setPendingToRemove((prev) => [...prev, currentImage.id]);
      setAllImages((prev) => prev.filter((img) => img.id !== currentImage.id));
    } else if (currentImage.hasOwnProperty('uid')) {
      setPendingToRemove((prev) => [...prev, currentImage.uid]);
      setAllImages((prev) => prev.filter((img) => img.uid !== currentImage.uid));
      setImages((prev) => prev.filter((img) => img.uid !== currentImage.uid));
    } else if (currentImage.file) {
      setImages((prev) => prev.filter((img) => img.uid !== currentImage.uid));
      setAllImages((prev) => prev.filter((img) => img.uid !== currentImage.uid));
    }
  };

  const onDialogClose = (isAgree) => {
    if (isAgree) {
      const payload = {
        new_images: images.map((img) => ({ uid: img.uid, file: validateFileName(img.file) })),
        to_remove: [...pendingToRemove],
      };

      if (onChange) onChange(payload);

      const output = allImages.filter((img) => !payload.to_remove.includes(img.id || img.uid));
      formik.setFieldValue(srcName, output);
      formik.setFieldValue(toRemoveName, payload.to_remove);
      formik.setFieldValue(
        newImagesName,
        payload.new_images.map((img) => img.file),
      );

      setInitialState(output);
      setImages([]);
      setPendingToRemove([]);
    } else {
      setPendingToRemove([]);
      setAllImages([...initialState]);
      setImages([]);
    }

    setDialogOpen(false);
    setDialogTitle('Set up your images');
    setDialogSubtitle(
      'Please select images from the image vault or upload a new one by clicking on the plus button below',
    );
    setdialogUseButton(true);
    setMainFormDisplay(true);
    setDialogWeight(setUpImageWeightDialog);
  };

  const generateInputValue = (images) => {
    return images.length ? `${images.length} images, click to manage` : 'Click to upload images';
  };

  return (
    <>
      <div style={{ display: 'flex' }}>
        <NibDialog
          open={isDialogOpen}
          title={dialogTitle}
          subtitle={dialogSubTitle}
          onClose={onDialogClose}
          titleClassname={'mb-[15px] text-center pt-[20px] !font-semibold uppercase'}
          subtitleClassname={'max-w-[310px] text-center mx-auto !font-medium !text-[#A2A2A2]'}
          paperProps={{
            style: {
              width: '100%',
              maxWidth: dialogWeight,
            },
          }}
        >
          <Button
            style={{
              position: 'relative',
              left: '50%',
              transform: 'translateX(-50%)',
              marginBottom: 10,
            }}
            variant="contained"
            color="primary"
            onClick={() => {
              setMainFormDisplay(!showMainForm);
              setdialogUseButton(false);
              setDialogWeight(showMainForm ? selectImageWeightDialog : setUpImageWeightDialog);
            }}
          >
            Select from Images
          </Button>

          <div
            id="gallery-block"
            style={{
              display: showMainForm ? 'block' : 'none',
              position: 'relative',
            }}
          >
            <RUG
              key={allImages.length}
              onClick={(image) => {
                if (image && image.source) {
                  window.open(image.source, '_blank');
                }
              }}
              accept={accept}
              onChange={onChng}
              onConfirmDelete={onRemove}
              source={(response) => response.url}
              customRequest={falseRequest}
              initialState={allImages}
            />
          </div>
          {!showMainForm && (
            <div style={{ height: '80vh' }}>
              <div className="absolute top-0 right-0 z-10 p-[10px]">
                <IconButton
                  style={{ zIndex: '999' }}
                  onClick={() => {
                    setDialogTitle('Set up your images');
                    setdialogUseButton(true);
                    setMainFormDisplay(!showMainForm);
                    setDialogWeight(setUpImageWeightDialog);
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </div>
              <Scrollbars style={{ width: '100%', height: '100%' }}>
                <GallerySelectImageTable
                  setdialogUseButton={setdialogUseButton}
                  showMainForm={showMainForm}
                  setMainFormDisplay={setMainFormDisplay}
                  setUpImageWeightDialog={setUpImageWeightDialog}
                  setDialogWeight={setDialogWeight}
                  falseRequest={falseRequest}
                />
              </Scrollbars>
            </div>
          )}
          <div className="w-full flex flex-col items-center justify-center gap-3">
            <NibButton
              className={`n-button-big-red ${!(images.length || toRemove.length || pendingToRemove.length) ? 'disabled' : ''}`}
              disabled={!(images.length || toRemove.length || pendingToRemove.length)}
              label="Save"
              onClick={() => onDialogClose(true)}
            />
            <NibButton label="Cancel" mui={true} variant="text" onClick={() => onDialogClose(false)} />
          </div>
        </NibDialog>
      </div>
      <NibInput
        inputStyles={{ cursor: 'pointer' }}
        onClick={onAdd}
        tools={tools}
        onAdd={onAdd}
        required={required}
        placeholder={placeholder}
        label={label}
        variant={'filled'}
        style={{ width: '100%' }}
        value={generateInputValue(initialState)}
        helperText={helperText}
      />
    </>
  );
};

export default InputImageGallery;
